import type { ReactNode } from 'react';
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { ClassNameProp } from '../common-types';

/**
 * Properties for {@link Tabs} component.
 */
export interface TabsProps extends ClassNameProp {
  /**
   * Individual tab components
   */
  children: ReactNode;
  /**
   * Pass label to set initial active Tab.
   */
  activeTab?: string;
  /**
   * The change callback.
   * @param label the selected Tab label as string.
   */
  onChange?: (label: string) => void;
}

interface TabContextType {
  selectedTab: string | null;
  handleTabClick: (tab: string) => void;
}

export const TabContext = createContext<TabContextType | undefined>(undefined);

/**
 * Component to manage multiple Tab components as a single group using a
 * label value to determine which Tab is active.
 *
 * @return the created {@link React.JSX.Element}.
 */
export function Tabs({
  className = '',
  children,
  activeTab,
  onChange,
  ...props
}: Readonly<TabsProps>): React.JSX.Element {
  const cName = `ehs-tabs 
  ${className ?? ''} `;
  const [selectedTab, setSelectedTab] = useState(activeTab ?? '');
  const tabListRef = useRef<HTMLDivElement>(null);

  // Function to update the selected children tab
  const handleTabClick = useCallback(
    (activeTab: string) => {
      setSelectedTab(activeTab);
      onChange?.(activeTab);
    },
    [onChange]
  );

  useEffect(() => {
    if (activeTab) {
      setSelectedTab(activeTab);
    }
  }, [activeTab]);

  //prop to the Context provider
  const contextValues = useMemo(
    () => ({
      selectedTab,
      handleTabClick
    }),
    [selectedTab, handleTabClick]
  );

  // Allow using arrowKeys to focus on non-active Tab
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const activeElement = e.target;
    if (
      !(activeElement instanceof HTMLElement) ||
      activeElement.getAttribute('role') !== 'tab'
    ) {
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      moveFocus(activeElement, +1);
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      moveFocus(activeElement, -1);
    }

    if (e.key === 'Home') {
      e.preventDefault();
      moveFocusToIndex(0);
    }

    if (e.key === 'End') {
      e.preventDefault();
      moveFocusToIndex(-1);
    }
  };

  const getEnabledTabs = () =>
    Array.from(
      tabListRef.current?.querySelectorAll<HTMLElement>('[role="tab"]') ?? []
    ).filter(tab => tab.getAttribute('aria-disabled') !== 'true');

  const focusAndSelectTab = (tab: HTMLElement | undefined) => {
    const tabLabel = tab?.dataset.label;
    if (!tab || !tabLabel) {
      return;
    }

    tab.focus();
    handleTabClick(tabLabel);
  };

  const moveFocusToIndex = (index: number) => {
    const tabItems = getEnabledTabs();
    if (tabItems.length === 0) {
      return;
    }

    const nextIndex = index === -1 ? tabItems.length - 1 : index;
    focusAndSelectTab(tabItems[nextIndex]);
  };

  // Depends on arrowKey, focus is put on the correct Tab
  const moveFocus = (activeTab: HTMLElement, move: number) => {
    const tabItems = getEnabledTabs();
    if (tabItems.length === 0) {
      return;
    }

    const activeIndex = tabItems.indexOf(activeTab);
    if (activeIndex === -1) {
      return;
    }

    const nextIndex = (activeIndex + move + tabItems.length) % tabItems.length;
    focusAndSelectTab(tabItems[nextIndex]);
  };

  return (
    <TabContext.Provider value={contextValues}>
      <div
        {...props}
        ref={tabListRef}
        className={cName.trim()}
        role="tablist"
        onKeyDown={handleKeyDown}
      >
        {children}
      </div>
    </TabContext.Provider>
  );
}
